import { useState, useEffect, useCallback } from 'react';
import DashBoardApi from '../../api/DashBoardApi';
import CampaignReportAPI from '../../api/CampaignReport';
import { SelectOption } from './dashboardTypes';

export function useFormOptions(selectedWebsite: string) {
  const [campaignOptions, setCampaignOptions] = useState<SelectOption[]>([]);
  const [goalOptions, setGoalOptions] = useState<SelectOption[]>([]);
  const [revenueGoalOptions, setRevenueGoalOptions] = useState<SelectOption[]>([]);
  const [reportOptions, setReportOptions] = useState<SelectOption[]>([]);
  const [campaignOptionsLoading, setCampaignOptionsLoading] = useState<boolean>(true);

  const fetchCampaignOptions = useCallback(async (goalId?: string) => {
    if (!selectedWebsite) {
      setCampaignOptions([]);
      setCampaignOptionsLoading(false);
      return;
    }
    setCampaignOptionsLoading(true);
    try {
      const params: any = { system_website_id: selectedWebsite };
      if (goalId) params.goal_id = goalId;
      const response = await DashBoardApi.getdashboardactivecampaignlist(params);
      const data = await response.json();
      if (data?.status === 1 && Array.isArray(data?.data)) {
        setCampaignOptions(
          data.data.map((item: any) => ({
            value: String(item.campaign_id),
            label: String(item.campaign_name || ''),
          }))
        );
      } else {
        setCampaignOptions([]);
      }
    } catch {
      setCampaignOptions([]);
    } finally {
      setCampaignOptionsLoading(false);
    }
  }, [selectedWebsite]);

  // Fetch goals present in ANY of the supplied campaigns (union). When no
  // campaignIds are supplied, the backend returns the full list for the
  // selected site (wizard initial-load mode).
  const fetchGoalOptions = useCallback(async (campaignIds?: string[]) => {
    if (!selectedWebsite) {
      setGoalOptions([]);
      setRevenueGoalOptions([]);
      return;
    }
    try {
      const response = await DashBoardApi.getdashboardgoallist({
        system_website_id: selectedWebsite || undefined,
        campaign_ids: campaignIds,
      });
      const data = await response.json();
      if (data?.status === 1 && Array.isArray(data?.data)) {
        const rows = data.data as Array<{ goal_id: string; goal_name: string; is_revenue_goal?: number }>;
        setGoalOptions(
          rows.map((item) => ({
            value: String(item.goal_id),
            label: String(item.goal_name || ''),
          }))
        );
        // Revenue Goal dropdown: only goals flagged as revenue-eligible in at
        // least one of the selected campaigns (include_aov = 1 OR sales_value > 0).
        setRevenueGoalOptions(
          rows
            .filter((item) => Number(item.is_revenue_goal) === 1)
            .map((item) => ({
              value: String(item.goal_id),
              label: String(item.goal_name || ''),
            }))
        );
      } else {
        setGoalOptions([]);
        setRevenueGoalOptions([]);
      }
    } catch {
      setGoalOptions([]);
      setRevenueGoalOptions([]);
    }
  }, [selectedWebsite]);

  const fetchReportOptions = useCallback(async () => {
    try {
      const response = await CampaignReportAPI.getreportlist({});
      const data = await response.json();
      if (data?.status === 1 && Array.isArray(data?.data?.report_list)) {
        setReportOptions(
          data.data.report_list.map((item: any) => ({
            value: String(item.report_id),
            label: String(item.report_name || ''),
          }))
        );
      }
    } catch { setReportOptions([]); }
  }, []);

  useEffect(() => { fetchCampaignOptions(); }, [fetchCampaignOptions]);
  useEffect(() => { fetchGoalOptions(); }, [fetchGoalOptions]);
  useEffect(() => { fetchReportOptions(); }, []);

  return {
    campaignOptions,
    campaignOptionsLoading,
    goalOptions,
    revenueGoalOptions,
    reportOptions,
    fetchCampaignOptions,
    fetchGoalOptions,
    fetchReportOptions,
  };
}
